import {APIUrl} from "./config";
import {getToken} from "./auth";

const BASE_URL = `${APIUrl}/api`;

async function request(method, url, data, options = {}, withAuth = true) {
    const headers = {};
    if (withAuth) {
        const token = getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
    }
    if (data !== undefined) headers['Content-Type'] = 'application/json';

    const res = await fetch(`${BASE_URL}${url}`, {
        method,
        headers,
        signal: options.signal,
        ...(data !== undefined && {body: JSON.stringify(data)}),
    });

    if (withAuth && res.status === 401) {
        window.dispatchEvent(new CustomEvent('api:unauthorized'));
        const err = new Error('Unauthorized');
        err.response = {data: null, status: 401};
        throw err;
    }

    let body;
    try {
        body = await res.json();
    } catch {
        body = null;
    }

    if (!res.ok) {
        const err = new Error(`HTTP ${res.status}`);
        err.response = {data: body, status: res.status};
        throw err;
    }

    return {data: body};
}

// Como `request`, pero para subir un FormData (multipart). No se fuerza
// Content-Type: el browser arma el boundary correcto sólo si lo deja vacío.
async function requestFile(method, url, formData, options = {}) {
    const headers = {};
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${BASE_URL}${url}`, {
        method,
        headers,
        signal: options.signal,
        body: formData,
    });

    if (res.status === 401) {
        window.dispatchEvent(new CustomEvent('api:unauthorized'));
        const err = new Error('Unauthorized');
        err.response = {data: null, status: 401};
        throw err;
    }

    let body;
    try {
        body = await res.json();
    } catch {
        body = null;
    }

    if (!res.ok) {
        const err = new Error(`HTTP ${res.status}`);
        err.response = {data: body, status: res.status};
        throw err;
    }

    return {data: body};
}

export const API = {
    get: (url, options) => request('GET', url, undefined, options),
    post: (url, data, options) => request('POST', url, data, options),
    put: (url, data, options) => request('PUT', url, data, options),
    delete: (url, options) => request('DELETE', url, undefined, options),
    postFile: (url, formData, options) => requestFile('POST', url, formData, options),
};

// For public, unauthenticated flows (e.g. candidate quiz links, password reset).
// Never attaches a JWT, even if a stale token exists in localStorage.
export const PublicAPI = {
    get: (url, options) => request('GET', url, undefined, options, false),
    post: (url, data, options) => request('POST', url, data, options, false),
    put: (url, data, options) => request('PUT', url, data, options, false),
    delete: (url, options) => request('DELETE', url, undefined, options, false),
};

export function getWithParams(url, params, options) {
    const queryString = new URLSearchParams();

    for (const [key, values] of Object.entries(params)) {
        values.forEach(value => queryString.append(key, value));
    }

    return API.get(`${url}?${queryString.toString()}`, options);
}
